import type { SchemaType } from '@based/schema'
import { seoMetaDescription, seoMetaTitle } from './pageSeo'
import { SchemaToType } from './utils'
import { INITIATIVE_USER_ROLES } from './initiativeRoles'

export * from './initiativeRoles'

export const PLOT_STATUS = {
  underOption: 'underOption',
  noPlot: 'noPlot',
  potentialPlotInMind: 'potentialPlotInMind',
} as const
export type PlotStatus = keyof typeof PLOT_STATUS

export const NUMBER_OF_HOMES = {
  from2To5: 'from2To5',
  from6To10: 'from6To10',
  from11to15: 'from11to15',
  from16to30: 'from16to30',
  from31to50: 'from31to50',
  from51to80: 'from51to80',
  from81to100: 'from81to100',
  moreThan100: 'moreThan100',
} as const
export type NumberOfHomes = keyof typeof NUMBER_OF_HOMES

export const LEGACY_NUMBER_OF_HOMES = {
  lessThan5: 'lessThan5',
  from5To10: 'from5To10',
  from11to30: 'from11to30',
  moreThan30: 'moreThan30',
} as const
export type LegacyNumberOfHomes = keyof typeof LEGACY_NUMBER_OF_HOMES

export const OWNERSHIP_MODE = {
  buy: 'buy',
  rent: 'rent',
  mix: 'mix',
  dontKnow: 'dontKnow',
} as const
export type OwnershipMode = keyof typeof OWNERSHIP_MODE

export const RENT_SEGMENT = {
  social: 'social',
  middle: 'middle',
  freeSector: 'freeSector',
} as const
export type RentSegment = keyof typeof RENT_SEGMENT

export const ONBOARDING_STEP = {
  livingVision: 'livingVision',
  images: 'images',
  initiativeDetails: 'initiativeDetails',
  livingAmenities: 'livingAmenities',
  livingTogether: 'livingTogether',
  location: 'location',
  contact: 'contact',
  finish: 'finish',
} as const
export type OnboardingStep = keyof typeof ONBOARDING_STEP

export const initiative = {
  uuid: { type: 'alias', format: 'UUID' },
  slug: { type: 'alias' },
  title: {
    type: 'string',
    title: 'Title of the initiative',
    description: 'this is where you put the title',
  },
  description: {
    type: 'text',
  },
  metaTitle: seoMetaTitle,
  metaDescription: seoMetaDescription,
  vision: {
    type: 'text',
  }, // TODO: this can go to content blocks
  contentBlocks: {
    items: {
      ref: 'pageContentBlock',
      prop: 'initiative',
      $position: ['overviewTab'],
      $positionIndex: 'number',
    },
  },
  //uniqueViews: 'cardinality',
  createdAt: {
    type: 'timestamp',
    on: 'create',
  },
  isLive: 'boolean',
  isTest: 'boolean',
  phase: { ref: 'phase', prop: 'initiatives' },
  developmentForm: { ref: 'developmentForm', prop: 'initiatives' },
  searchingForMembers: 'boolean',
  searchingForMembersDescription: 'text', // TODO: this can go to content blocks
  sharingCommonSpaces: 'boolean',
  hasAutomaticMemberApproval: 'boolean',
  location: {
    props: {
      geo: {
        props: {
          lat: 'number',
          long: 'number',
        },
      },
      address: {
        props: {
          country: { ref: 'country', prop: 'initiatives' },
          region: { ref: 'region', prop: 'initiatives' },
          municipality: { ref: 'municipality', prop: 'initiatives' },
          locality: { ref: 'locality', prop: 'initiatives' },
          street: 'string',
          postalCode: 'string',
        },
      },
    },
  },
  plotApplications: {
    items: { ref: 'plotApplication', prop: 'initiative' },
  },
  plot: { ref: 'plot', prop: 'selectedInitiative' },
  followingPlots: { items: { ref: 'plot', prop: 'followingInitiatives' } },
  updatedAt: {
    type: 'timestamp',
    on: 'update',
  },
  images: {
    items: { ref: 'file', prop: 'initiativeImage' },
  },
  users: {
    items: {
      prop: 'initiatives',
      ref: 'user',
      $role: Object.values(INITIATIVE_USER_ROLES),
      $followerSince: { type: 'timestamp' },
      $appliedAt: { type: 'timestamp' },
      $memberSince: { type: 'timestamp' },
      $adminSince: { type: 'timestamp' },
    },
  },
  housingForms: { items: { ref: 'housingForm', prop: 'initiatives' } },
  serviceProviderConcepts: {
    items: { ref: 'serviceProviderConcept', prop: 'initiatives' },
  },
  livingEnvironments: {
    items: { ref: 'livingEnvironment', prop: 'initiatives' },
  },
  targetAudiences: { items: { ref: 'targetAudience', prop: 'initiatives' } },
  interests: { items: { ref: 'interest', prop: 'initiatives' } },
  numberOfHomes: Object.values(NUMBER_OF_HOMES),
  legacyNumberOfHomes: Object.values(LEGACY_NUMBER_OF_HOMES),
  projectSize: 'string',
  ownershipMode: Object.values(OWNERSHIP_MODE),
  buyBudget: { ref: 'buyBudget', prop: 'initiatives' },
  rentalBudget: { ref: 'rentBudget', prop: 'initiatives' },
  potentialPlotStatus: Object.values(PLOT_STATUS),
  rentSegment: Object.values(RENT_SEGMENT),
  potentialPlotAddress: 'string',
  permit: 'boolean',
  financingRequirementsConstruction: 'boolean',
  financingRequirementsPlanning: 'boolean',
  kvkRegistration: 'boolean',
  contactFirstName: 'string',
  contactLastName: 'string',
  contactEmail: { type: 'string', format: 'email' },
  contactPhoneNumber: 'string',
  scheduledCall: 'timestamp',
  isCalendlyScheduled: 'boolean',
  channels: { items: { ref: 'channel', prop: 'initiative' } },
  thread: { ref: 'thread', prop: 'initiative' },
  events: {
    items: { ref: 'event', prop: 'initiative' },
  },
  serviceProviders: { items: { ref: 'serviceProvider', prop: 'initiatives' } },
  spotlights: {
    items: { ref: 'spotlight', prop: 'initiative' },
  },
  amenities: { items: { ref: 'amenity', prop: 'initiatives' } },
  customAmenities: { items: { ref: 'amenity', prop: 'eligibleInitiatives' } },
  onboardingStep: Object.values(ONBOARDING_STEP),
  plotRegistryApplications: {
    items: {
      ref: 'plotRegistryApplication',
      prop: 'initiative',
    },
  },
  relatedTopicCommunities: {
    items: { ref: 'topicCommunity', prop: 'relatedInitiatives' },
  },
} satisfies SchemaType

export type Initiative = SchemaToType<typeof initiative> & {
  id: number
}
